import React, { useState, useMemo, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import api from '../api';
import './PasswordSetupModal.css';

const PasswordSetupModal = ({ onClose }) => {
  const { updateUser } = useContext(AuthContext);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const checks = useMemo(() => ({
    length: password.length >= 8,
    alpha: /[a-zA-Z]/.test(password),
    numeric: /[0-9]/.test(password),
  }), [password]);

  const allValid = checks.length && checks.alpha && checks.numeric;
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const canSubmit = allValid && passwordsMatch && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError('');
    try {
      await api.post('/api/auth/set-password', { password });
      updateUser({ has_password: true });
      onClose();
    } catch (err) {
      console.error('Set password error:', err);
      const serverMsg = err.response?.data?.error || err.response?.data?.msg;
      setError(serverMsg || err.message || 'Failed to set password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="psm-overlay">
      <div className="psm-card" onClick={(e) => e.stopPropagation()}>
        <div className="psm-header">
          <div className="psm-icon">🔐</div>
          <h2>Set Up Your Password</h2>
          <p>Add a password so you can sign in even without Google. You can skip this for now.</p>
        </div>

        {error && <div className="psm-error">{error}</div>}

        <form className="psm-form" onSubmit={handleSubmit} noValidate>
          <div className="psm-field">
            <label>Password</label>
            <div className="psm-input-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter a password"
                autoFocus
              />
              <button
                type="button"
                className="psm-eye"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <div className="psm-field">
            <label>Confirm Password</label>
            <div className="psm-input-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
              />
            </div>
            {confirmPassword.length > 0 && !passwordsMatch && (
              <div className="psm-mismatch">Passwords do not match</div>
            )}
          </div>

          <ul className="psm-checks">
            <li className={checks.length ? 'met' : ''}>
              <span className="psm-check-icon">{checks.length ? '✓' : '○'}</span>
              At least 8 characters
            </li>
            <li className={checks.alpha ? 'met' : ''}>
              <span className="psm-check-icon">{checks.alpha ? '✓' : '○'}</span>
              At least 1 letter (a–z)
            </li>
            <li className={checks.numeric ? 'met' : ''}>
              <span className="psm-check-icon">{checks.numeric ? '✓' : '○'}</span>
              At least 1 number (0–9)
            </li>
          </ul>

          <div className="psm-actions">
            <button type="button" className="psm-skip" onClick={onClose} disabled={submitting}>
              Skip for now
            </button>
            <button type="submit" className="psm-submit" disabled={!canSubmit}>
              {submitting ? 'Saving...' : 'Set Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PasswordSetupModal;
